/**
 * Icon spotlight for the exhibition guided tour.
 *
 * The tour names toolbar icons while it talks, so the visitor has to find the
 * icon being described within the sentence that describes it. Highlighting is
 * done by marking the real button rather than drawing a copy of it: the bars
 * re-render on viewer state, and a copied overlay would drift out of place the
 * moment a button becomes enabled, disabled, or hidden on a narrow screen.
 *
 * Deliberately no full-screen dimming layer. The design-process lessons open
 * real dialogs while the highlight is up, and an overlay that covers the page
 * would either swallow those dialogs' clicks or have to be punched through on
 * every re-render.
 */

/** Stable ids placed on the bars with data-tour-id. Bars own the attribute; the tour owns this list. */
export const TOUR_TARGETS = {
  // Top bar — Toolbar.tsx
  database: "database",
  patient: "patient",
  scan: "scan",
  pressureImport: "pressure-import",
  tsolesImport: "tsoles-import",
  wbct: "wbct",
  loadTemplate: "load-template",
  alignPressure: "align-pressure",
  output: "output",
  modelCategory: "model-category",
  print: "print",
  // Left bar — SideToolbar.tsx
  labels: "labels",
  coloring: "coloring",
  camera: "camera",
  wireframe: "wireframe",
  clipping: "clipping",
  grid: "grid",
  feetView: "feet-view",
  // Right bar — PropertyPanel.tsx tabs
  tabView: "tab-view",
  tabAdditions: "tab-additions",
  tabEdit: "tab-edit",
  tabEngraving: "tab-engraving",
  tabProject: "tab-project",
  // Bottom bar — Footer.tsx
  measurement: "measurement",
  theme: "theme",
  settings: "settings",
  tickets: "tickets",
  tutorial: "tutorial",
} as const;

/**
 * The mobile and tablet layout, which is a different interface rather than a
 * narrower one: below 1250px the desktop toolbar is hidden entirely and the
 * same actions live behind three buttons — a menu at the top left holding what
 * the top bar carries, and an eye and a pencil at the top right standing in for
 * the left and right bars.
 */
export const TOUR_TARGETS_MOBILE = {
  // Top left: the drawer, and what is inside it.
  menu: "m-menu",
  newDesign: "m-new-design",
  database: "m-database",
  template: "m-template",
  patient: "m-patient",
  align: "m-align",
  modelCategory: "m-model-category",
  print: "m-print",
  settings: "m-settings",
  save: "m-save",
  // Top right, the eye: the view controls.
  viewMenu: "m-view-menu",
  labels: "m-labels",
  coloring: "m-coloring",
  camera: "m-camera",
  wireframe: "m-wireframe",
  clipping: "m-clipping",
  grid: "m-grid",
  feetView: "m-feet-view",
  measurement: "m-measurement",
  // Top right, the pencil: the design panels.
  editMenu: "m-edit-menu",
  tabView: "m-tab-view",
  tabEdit: "m-tab-edit",
  tabAdditions: "m-tab-additions",
  tabEngraving: "m-tab-engraving",
  tabProject: "m-tab-project",
  drawerClose: "m-drawer-close",
  theme: "m-theme",
  tutorial: "m-tutorial",
} as const;

/**
 * Ids inside the panels, rather than on the bars.
 *
 * The panels are the same components in both layouts — a phone opens the very
 * same Patient Info modal the desktop does — so these are shared rather than
 * duplicated into the two maps above.
 */
export const TOUR_PANEL_TARGETS = {
  // Patient Info opens on whichever of its two tabs the design has. The
  // pressure one is rendered only when a scan is loaded.
  patientTabPressure: "patient-tab-pressure",
  patientTabInfo: "patient-tab-info",
} as const;

/**
 * Shut whatever the previous section left open.
 *
 * Each mobile section works inside an overlay — the drawer, or one of the two
 * menus — and they stack rather than replace each other. The drawer in
 * particular cannot be toggled shut from the button that opened it, because
 * that handler only ever opens, so it needs its own close control. The menus
 * are MUI popovers and close on their backdrop.
 */
export function closeMobileOverlays(): void {
  const drawerClose = nodesFor(TOUR_TARGETS_MOBILE.drawerClose)[0];
  if (drawerClose) {
    const rect = drawerClose.getBoundingClientRect();
    if (rect.width > 0 && rect.height > 0) drawerClose.click();
  }
  // Only the backdrop that is actually up. keepMounted leaves hidden backdrops
  // in the DOM, and clicking those dismissed state nobody had opened.
  document
    .querySelectorAll<HTMLElement>(".MuiBackdrop-root")
    .forEach((backdrop) => {
      if (isNodeVisible(backdrop)) backdrop.click();
    });
}

/**
 * Whether the app is showing its mobile/tablet layout.
 *
 * Read from the viewport, matching the 1250px breakpoint the stylesheet uses.
 * It used to probe whether the hamburger was visible, which was wrong for a
 * subtle reason: while a menu is open MUI marks the rest of the page hidden, so
 * the probe failed exactly when the tour needed it — and the tour then reached
 * for the desktop targets while a mobile menu stood open.
 */
export const MOBILE_LAYOUT_MAX_WIDTH = 1250;

export function isMobileLayout(): boolean {
  if (typeof window === "undefined") return false;
  return window.innerWidth <= MOBILE_LAYOUT_MAX_WIDTH;
}

/** Bar containers, for the step that introduces a whole bar before naming its icons. */
export const TOUR_BARS = {
  top: ".toolbar",
  left: ".side-toolbar",
  right: ".property-panel",
} as const;

export type TourTarget = (typeof TOUR_TARGETS)[keyof typeof TOUR_TARGETS];

const HIGHLIGHT_CLASS = "tour-highlight";
const GROUP_CLASS = "tour-highlight-group";

/** Ids currently lit, so a re-render can restore them onto fresh DOM nodes. */
let activeTargets: string[] = [];
let activeGroup: string | null = null;
let observer: MutationObserver | null = null;

function nodesFor(id: string): HTMLElement[] {
  return Array.from(document.querySelectorAll<HTMLElement>(`[data-tour-id="${id}"]`));
}

/** True while paint() is writing, so the observer ignores its own mutations. */
let painting = false;
let paintFrame = 0;

/** Coalesce repaints to one per frame. */
function schedulePaint(): void {
  if (typeof window === "undefined") {
    paint();
    return;
  }
  if (paintFrame) return;
  paintFrame = window.requestAnimationFrame(() => {
    paintFrame = 0;
    paint();
  });
}

function paint(): void {
  if (painting) return;
  painting = true;
  try {
    paintNodes();
  } finally {
    painting = false;
  }
}

function paintNodes(): void {
  document.querySelectorAll<HTMLElement>(`.${HIGHLIGHT_CLASS}`).forEach((node) => {
    if (!activeTargets.includes(node.dataset.tourId ?? "")) {
      node.classList.remove(HIGHLIGHT_CLASS);
    }
  });
  activeTargets.forEach((id) => {
    nodesFor(id).forEach((node) => node.classList.add(HIGHLIGHT_CLASS));
  });

  document.querySelectorAll<HTMLElement>(`.${GROUP_CLASS}`).forEach((node) => {
    node.classList.remove(GROUP_CLASS);
  });
  if (activeGroup) {
    document.querySelectorAll<HTMLElement>(activeGroup).forEach((node) => {
      node.classList.add(GROUP_CLASS);
    });
  }
}

/**
 * The bars re-render whenever viewer state changes, which replaces the very
 * nodes carrying the highlight class. Re-applying on mutation keeps a highlight
 * alive for the whole sentence that describes it.
 */
function ensureObserver(): void {
  if (observer || typeof MutationObserver === "undefined") return;
  observer = new MutationObserver(() => {
    if (!activeTargets.length && !activeGroup) return;
    // paint() writes class attributes, which this observer would see as another
    // mutation: paint -> mutation -> paint, forever. Ignoring our own writes and
    // coalescing to one repaint per frame is what keeps that from locking the
    // page up.
    if (painting) return;
    schedulePaint();
  });
  // Attributes as well as children, because a MUI popover rendered with
  // keepMounted has its items in the DOM the whole time: opening it changes
  // style and aria-hidden rather than adding nodes.
  //
  // "class" is deliberately absent from the filter — that is the attribute
  // paint() writes, and watching it is what caused the loop.
  observer.observe(document.body, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ["style", "aria-hidden", "hidden"],
  });
}

/**
 * Re-mark a few times after a highlight is asked for, then stop.
 *
 * A target can arrive late: a drawer mounts its contents on open, and a popover
 * animates. Three widely spaced retries cover that without polling.
 */
let repaintTimers: number[] = [];
function repaintFor(): void {
  if (typeof window === "undefined") return;
  clearRepaints();
  repaintTimers = [150, 500, 1100].map((delay) =>
    window.setTimeout(() => {
      if (activeTargets.length || activeGroup) paint();
    }, delay)
  );
}

function clearRepaints(): void {
  repaintTimers.forEach((id) => window.clearTimeout(id));
  repaintTimers = [];
}

/** Light one or more icons. Replaces whatever was lit before. */
export function highlightTargets(targets: string | string[]): void {
  activeTargets = (Array.isArray(targets) ? targets : [targets]).filter(Boolean);
  ensureObserver();
  paint();
  repaintFor();
  scrollFirstIntoView();
}

/**
 * Light a whole bar at once, for the step that introduces it. Takes a selector
 * because a bar is a container, not a tour target.
 */
export function highlightGroup(selector: string | null): void {
  activeGroup = selector;
  ensureObserver();
  paint();
}

/** A bar can overflow on a phone; bring the described icon into view before talking about it. */
function scrollFirstIntoView(): void {
  const first = activeTargets.length ? nodesFor(activeTargets[0])[0] : undefined;
  if (!first?.scrollIntoView) return;
  try {
    first.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" });
  } catch {
    /* older browsers reject the options object */
  }
}

/**
 * Click a bar button, for the steps that have to reveal a dropdown before they
 * can point at what is inside it. Returns false when the button is not on
 * screen, so a step can skip itself rather than describe something invisible.
 */
export function clickTarget(id: string): boolean {
  const node = nodesFor(id).find(isNodeVisible) ?? nodesFor(id)[0];
  if (!node) return false;
  node.click();
  return true;
}

/**
 * Whether a target is actually on screen.
 *
 * Presence in the DOM is not enough. Below 1250px the whole desktop toolbar is
 * `display: none`, so its buttons still match a selector while being invisible
 * — and the tour would narrate icons the visitor cannot see. A zero-sized rect
 * is the reliable signal for that, since it catches a hidden ancestor too.
 */
function isNodeVisible(node: HTMLElement): boolean {
  const rect = node.getBoundingClientRect();
  if (rect.width <= 0 || rect.height <= 0) return false;

  // A size alone is not enough. The mobile menus are MUI popovers rendered with
  // keepMounted, so while closed their items stay in the DOM and keep their
  // layout — only `visibility: hidden` is applied to the wrapper. Measuring
  // rects alone therefore reported closed menu items as visible, and the tour
  // never opened the menu it was about to point inside.
  //
  // Deliberately NOT testing aria-hidden: MUI puts that on the *background*
  // while a modal is open, so checking it made every bar in the app read as
  // invisible the moment a menu opened — which is what stopped the menu item
  // from being marked at all.
  for (let el: HTMLElement | null = node; el; el = el.parentElement) {
    const style = window.getComputedStyle(el);
    if (style.display === "none" || style.visibility === "hidden") return false;
  }
  return true;
}

export function isTargetVisible(id: string): boolean {
  return nodesFor(id).some(isNodeVisible);
}

/**
 * Whether pressing this target would actually do anything.
 *
 * A disabled button is still on screen and still worth naming, so a step that
 * describes one is right to run. A step that *opens* it is not: the click is a
 * no-op, and the walkthrough would then sit out the whole hold waiting for a
 * panel that never appeared.
 */
export function isTargetActionable(id: string): boolean {
  const node = nodesFor(id).find(isNodeVisible);
  if (!node) return false;
  if ((node as HTMLButtonElement).disabled) return false;
  if (node.getAttribute("aria-disabled") === "true") return false;
  if (node.classList.contains("Mui-disabled")) return false;
  return true;
}

/**
 * Whether this tab is the one already on screen.
 *
 * A panel opens on one of its tabs, and which one depends on the design. The
 * walkthrough steps through the rest rather than clicking the one it arrived
 * on, which would hold an unchanged screen for a second time.
 */
export function isTargetSelected(id: string): boolean {
  const node = nodesFor(id).find(isNodeVisible);
  return node?.getAttribute("aria-selected") === "true";
}

/**
 * The modals that count as a panel the walkthrough has opened.
 *
 * The drawer and the two mobile menus are MUI modals as well, and the
 * walkthrough opens those on purpose — it must not mistake one for a panel it
 * has to put away again, or each section would dismiss the very menu whose
 * items it is about to point at. They are excluded by class rather than by
 * position, because which of them is on top changes between layouts.
 */
function panelModals(): HTMLElement[] {
  return Array.from(document.querySelectorAll<HTMLElement>(".MuiModal-root")).filter(
    (node) =>
      !node.classList.contains("MuiDrawer-root") &&
      !node.classList.contains("MuiPopover-root") &&
      !node.classList.contains("MuiMenu-root") &&
      isNodeVisible(node)
  );
}

/** True while one of the app's panels is up. */
export function isPanelOpen(): boolean {
  return panelModals().length > 0;
}

/**
 * Put the open panel away by clicking its backdrop, the way a tap outside does.
 *
 * Needed as well as the app's own close events, not instead of them: on a phone
 * these panels are opened from the drawer and held in the drawer's own state,
 * which nothing outside that component can clear.
 */
export function dismissTopPanel(): boolean {
  const panel = panelModals().pop();
  const backdrop = panel?.querySelector<HTMLElement>(".MuiBackdrop-root");
  if (!backdrop) return false;
  backdrop.click();
  return true;
}

/** Same test for a whole bar, which the bar-introduction steps hang on. */
export function isGroupVisible(selector: string): boolean {
  return Array.from(document.querySelectorAll<HTMLElement>(selector)).some(isNodeVisible);
}

/** Whether a target is currently rendered, so the tour can skip absent buttons. */
export function hasTarget(id: string): boolean {
  return isTargetVisible(id);
}

/** Drop every highlight. Safe to call when nothing is lit. */
export function clearHighlights(): void {
  activeTargets = [];
  activeGroup = null;
  clearRepaints();
  paint();
  observer?.disconnect();
  observer = null;
}
